import { datadogLogs } from '@datadog/browser-logs';

import type { Destination } from '@avodah-inc/med-logger';

import {
  sanitizeDatadogEvent,
  sanitizeSensitiveString,
} from './datadog-sanitizer';

let _logsInitialized = false;

export type DatadogLogsConfig = {
  clientToken: string;
  site: string;
  env: string;
  version?: string;
  service?: string;
};

/**
 * Browser Destination that forwards med-logger (pino) JSON output
 * to the Datadog Browser Logs SDK.
 *
 * This is the SINGLE file in the entire app that imports \@datadog/browser-logs.
 * All other code uses med-logger's Logger API.
 */
export class DatadogBrowserDestination implements Destination {
  /**
   * Initialize the Datadog Browser Logs SDK.
   * Idempotent. Must be called once before any logs are written.
   * Preserves all original configuration including sanitizer.
   */
  static init(config: DatadogLogsConfig): void {
    if (_logsInitialized) return;
    _logsInitialized = true;

    datadogLogs.init({
      clientToken: config.clientToken,
      site: config.site,
      env: config.env,
      version: config.version,
      forwardConsoleLogs: 'all',
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      service: config.service ?? 'nsight-web',
      beforeSend: (event) => {
        if (event.message) {
          event.message = sanitizeSensitiveString(event.message);
        }
        sanitizeDatadogEvent(event);
        return true;
      },
    });

    datadogLogs.setGlobalContextProperty('user_business', 'med');
    datadogLogs.setGlobalContextProperty('user_env', config.env);
  }

  write(chunk: string): void {
    const lines = chunk.split('\n').filter(Boolean);
    for (const line of lines) {
      try {
        const parsed = JSON.parse(line) as Record<string, unknown>;
        const level = (parsed.level ?? 'info') as string;
        const message = (parsed.message ?? '') as string;
        const {
          level: _l,
          time: _t,
          message: _m,
          hostname: _h,
          pid: _p,
          service: _s,
          env: _e,
          version: _v,
          ...context
        } = parsed;

        switch (level) {
          case 'error':
          case 'fatal': {
            const errorInfo = parsed.error as
              | Record<string, unknown>
              | undefined;
            const err = errorInfo
              ? Object.assign(new Error(errorInfo.message as string), {
                  stack: errorInfo.stack,
                })
              : new Error(message);
            datadogLogs.logger.error(message, context, err);
            break;
          }
          case 'warn':
            datadogLogs.logger.warn(message, context);
            break;
          case 'debug':
            datadogLogs.logger.debug(message, context);
            break;
          default:
            datadogLogs.logger.info(message, context);
        }
      } catch {
        // Malformed log line, skip.
      }
    }
  }
}
